import React, {
  useState,
  SyntheticEvent,
  useEffect,
  ReactNode,
  cloneElement,
  useRef,
} from "react";
import {
  Autocomplete,
  TextField,
  Checkbox,
  Box,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { getSearchData } from "@shared/utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import { showPopupModal } from "@widgets/CommonModal";
import { StyleSettings } from "../../styles/StyleSettings";
import MultiPopup from "@widgets/commonpopup/MultiPopup";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import EmpPopup from "@widgets/commonpopup/EmpPopup";
import { showIssueModal } from "@widgets/IssueModal";

interface SnMultiCodeNameBoxProps {
  defaultValue?: any;
  code: string;
  cd_clas?: string;
  onValueChanged?: (code: string, name: string) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
  popupConditions?: any;
}

const SnMultiCodeNameBox = React.memo(
  ({
    defaultValue,
    code,
    cd_clas,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    inputRef,
    isSearchPanel = false,
    onFocus,
    popupConditions = {},
  }: SnMultiCodeNameBoxProps) => {
    const { userInfo } = global();
    const [options, setOptions] = useState<any[]>(defaultValue);
    const [selected, setSelected] = useState<any[]>(defaultValue);
    const [inputValue, setInputValue] = useState("");
    const [displayValue, setDisplayValue] = useState(
      defaultValue ? defaultValue.map((item: any) => item.name).join(", ") : ""
    );
    const [isFocused, setIsFocused] = useState(false);
    const [borderWidth, setBorderWidth] = useState(isRequired ? "2px" : "1px");
    const [open, setOpen] = React.useState(false);

    // tab, enter, blur 이벤트 제어용
    const isOpenRef = useRef(false);

    // 팝업 생성 시 필요한 state
    const [title, setTitle] = useState<string>("");
    const [width, setWidth] = useState<any>();
    const [height, setHeight] = useState<any>();
    const [autoSearch, setAutoSearch] = useState<boolean>(true);

    const fetchDefaultValue = async () => {
      // name값을 가져와야 하니까 일단 보류
      try {
        if (defaultValue) {
          setOptions(defaultValue);
          setSelected(defaultValue);

          const names = defaultValue.map((item: any) => item.name).join(", ");
          setDisplayValue(names);
        }
      } catch (error: any) {}
    };

    const fetchIsRequired = async () => {
      try {
        if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired]);

    useEffect(() => {
      const multiPopupData = getSearchData(code, undefined);
      setTitle(multiPopupData.title);
      setAutoSearch(multiPopupData.autoSearch);
      setWidth(multiPopupData.width);
      setHeight(multiPopupData.height);
    }, [code]);

    useEffect(() => {
      if (!isFocused && selected.length) {
        setDisplayValue(selected.map((item) => item.name).join(", "));
      }
    }, [selected, isFocused]);

    const openPopup = async () => {
      if (isReadOnly) return showDialog("alter", "읽기 전용입니다.");
      if (isOpenRef.current) return;

      isOpenRef.current = true;
      try {
        const mergedData = {
          ...popupConditions,
          str_search: inputValue,
          cd_clas: cd_clas || "",
        };
        let result;
        if (code === "POPUP_RECIPIENT" || code === "POPUP_REFERENCE") {
          const multiPopup = (
            <EmpPopup code={code} selectedData={mergedData} options={[]} />
          );

          result = await showIssueModal({
            code: code,
            title: title,
            children: multiPopup,
            width: width,
            height: height,
          });
        } else {
          const multiPopup = (
            <MultiPopup
              code={code}
              selectedData={mergedData}
              options={options}
              autoSearch={autoSearch}
            />
          );

          result = await showPopupModal({
            title: title,
            children: multiPopup,
            width: width,
            height: height,
          });
        }
        const data = handleReturnFunction(result);
        if (data === "close") {
          // onFocus && onFocus("current");
          setOpen(true);
          return;
        }
        if (data.length) {
          setOptions(data);
          setSelected(data);
          onValueChanged?.(
            data.map((item: any) => item.code).join(","),
            data.map((item: any) => item.name).join(",")
          );
          if (isSearchPanel) {
            setIsFocused(true);
            setOpen(true);
            onFocus && onFocus("current");
          } else {
            setIsFocused(false);
            onFocus && onFocus("next");
          }
        } else {
          setOptions([]);
          setSelected([]);
          setDisplayValue("");
          onValueChanged?.("", "");
        }
      } finally {
        setTimeout(() => {
          isOpenRef.current = false;
        }, 100);
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = async (key?: string) => {
      // 이미 팝업 열려있으면 return
      if (isOpenRef.current) return;

      if (!inputValue) {
        // 입력값이 없을 때
        if ((key === "Enter" || key === "searchBtn") && !isOpenRef.current) {
          await openPopup();
        } else {
          if (key === "Tab") {
            // setIsFocused(false);
            onFocus && onFocus("next");
          }
          setIsFocused(false);
        }
      } else {
        // 입력값이 있을 때
        if (key === "searchBtn") {
          await openPopup();
        } else {
          const data = await checkData();
          if (data && data.length === 1) {
            setOptions(data);
            setSelected(data);
            onValueChanged?.(data[0].code, data[0].name);
            if (isSearchPanel) {
              setIsFocused(true);
              setOpen(true);
              onFocus && onFocus("current");
            } else {
              setInputValue("");
              setIsFocused(false);
              onFocus && onFocus("next");
            }
          } else if (!isOpenRef.current) {
            setInputValue("");
            await openPopup();
          }
        }
      }
    };

    // 선택 변경 핸들러
    const handleSelectChange = (
      e: SyntheticEvent,
      value: any[],
      reason: any
    ) => {
      if (reason === "clear") {
        setOptions([]);
        setSelected([]);
        setDisplayValue("");
        onValueChanged?.("", "");
        // return;
      }

      setSelected(value);
      onValueChanged?.(
        value.map((v) => v.code).join(","),
        value.map((v) => v.name).join(",")
      );
      setInputValue(inputValue); // 선택 후에도 검색어 유지
    };

    // 검색어 변경 핸들러
    const handleInputChange = (e: SyntheticEvent, value: string) => {
      setInputValue(value); // 검색어 업데이트
    };

    // 공백 상태에서 backspace 방지 핸들러
    const handleKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Backspace" && !inputValue) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        await handleBlur(e.key);
      }
    };

    /**
     * code, like 검색으로 데이터 검증
     */
    const checkData = async () => {
      try {
        const popupSetting = PopupSettings[code];
        if (!popupSetting) return;

        const param1 = {
          id: code.toLowerCase() + "_02",
          params: {
            cd_firm: userInfo.cd_firm,
            [popupSetting.str_search]: inputValue,
            cd_clas: cd_clas || "",
          },
        };
        const result1 = await fnGetDataSet(param1);
        if (result1[0]?.length === 1) return handleReturnFunction(result1[0]);

        const param2 = {
          id: code.toLowerCase() + "_03",
          params: { cd_firm: userInfo.cd_firm, str_search: inputValue },
          cd_clas: cd_clas || "",
        };
        const result2 = await fnGetDataSet(param2);
        if (result2[0]?.length === 1) return handleReturnFunction(result2[0]);
      } catch (e) {}
    };

    /**
     * 팝업 데이터에 code, name 추가
     * @param dataArr 팝업 데이터
     */
    const handleReturnFunction = (data: any) => {
      const popupSetting = PopupSettings[code];
      if (!popupSetting || !data) return [];
      if (data === "close") {
        return data;
      } else {
        return data.map((d: any) => ({
          ...d,
          code: d[popupSetting.columns[0]],
          name: d[popupSetting.columns[1]],
        }));
      }
    };

    // 팝업 아이콘 추가하는 함수
    const addSearchIcon = (endAdornment: ReactNode) => {
      // endAdornment가 React element가 아닌 경우 그대로 반환
      if (!React.isValidElement(endAdornment)) return endAdornment;

      // 드롭다운 화살표 버튼을 먼저 배치하고, 그 다음에 검색 버튼을 배치
      const children = React.Children.toArray(endAdornment.props.children);
      children.push(
        <IconButton
          key="search-button"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            handleBlur("searchBtn");
          }}
          sx={{
            width: 28,
            height: 28,
            marginLeft: "2px",
          }}
          edge="end"
          tabIndex={-1}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      );

      return cloneElement(endAdornment, {}, children);
    };

    return (
      <Box sx={{ position: "relative" }}>
        <Autocomplete
          // disablePortal
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          multiple
          disableCloseOnSelect
          options={options}
          value={selected}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={handleSelectChange}
          onFocus={handleFocus}
          onBlur={() => handleBlur()}
          clearOnBlur={false}
          disabled={isReadOnly}
          getOptionLabel={(option) => option.name || ""}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          filterOptions={(options, { inputValue }) =>
            inputValue
              ? options.filter((option) =>
                  option.name.toLowerCase().includes(inputValue.toLowerCase())
                )
              : options
          }
          renderTags={() => null}
          renderInput={(params) => (
            <TextField
              {...params}
              onKeyDown={handleKeyDown}
              slotProps={{
                input: {
                  ...params.InputProps,
                  inputRef: inputRef,
                  startAdornment:
                    !isFocused && displayValue ? (
                      <span
                        className="custom-adornment"
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "calc(100% - 90px)",
                        }}
                      >
                        {displayValue}
                      </span>
                    ) : null,
                  endAdornment: addSearchIcon(params.InputProps.endAdornment),
                  style: {
                    height: StyleSettings.control_height,
                    fontSize: StyleSettings.font,
                  },
                },
              }}
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.code}>
              <Checkbox checked={selected} />
              <Box component="span" sx={{ flexGrow: 1 }}>
                {option.name}
              </Box>
            </li>
          )}
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              padding: 0,
              backgroundColor: "white",
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
            },
            "& .MuiInputBase-input": {
              fontSize: StyleSettings.font,
            },
            "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
              padding: "0",
              paddingLeft: "14px",
              fontSize: StyleSettings.font,
            },
            "& .custom-adornment": {
              // focus 시, dropdown 보이는 style
              position: "absolute",
              left: "14px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "calc(100% - 60px)",
              pointerEvents: "none",
            },
            "& .MuiAutocomplete-option": {
              padding: 0,
            },
          }}
        />
      </Box>
    );
  }
);
export default SnMultiCodeNameBox;
